/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-18
 */

import { useEffect } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { Box, Typography, Alert, Chip } from '@mui/material';
import { DeveloperMode } from '@mui/icons-material';

interface TurnstileWidgetProps {
  onSuccess: (token: string) => void;
  onError: () => void;
  onExpire: () => void;
  disabled: boolean;
}

const defaultProps: Partial<TurnstileWidgetProps> = {
  onError: () => {},
  onExpire: () => {},
  disabled: false,
};

/**
 * Cloudflare Turnstile widget component for bot protection.
 * Automatically bypasses verification in development mode.
 * @example
 * return (
 *   <TurnstileWidget
 *     onSuccess={(token) => setTurnstileToken(token)}
 *     onError={() => setTurnstileError(true)}
 *   />
 * )
 * @returns {React.ReactElement} component.
 */
const TurnstileWidget = (
  props: Partial<TurnstileWidgetProps> & Pick<TurnstileWidgetProps, 'onSuccess'>
) => {
  const { onSuccess, onError, onExpire, disabled } = {
    ...defaultProps,
    ...props,
  };
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';
  const isTestMode =
    process.env.NODE_ENV === 'test' ||
    process.env.REACT_APP_TEST_MODE === 'true' ||
    (typeof window !== 'undefined' && window.location.hostname === 'localhost');

  // Auto-bypass in development or test mode
  useEffect(() => {
    if ((isDevMode || isTestMode) && !disabled) {
      // Automatically provide dev bypass token in development/test
      onSuccess('dev-bypass-token');
    }
  }, [isDevMode, isTestMode, disabled, onSuccess]); // onSuccess is now stable due to useCallback

  // Development mode bypass
  if (isDevMode) {
    return (
      <Box sx={{ my: 2 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, fontSize: '0.875rem' }}
        >
          Verification (Development Mode):
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            opacity: disabled ? 0.5 : 1,
          }}
        >
          <Chip
            icon={<DeveloperMode />}
            label="Development Bypass Active"
            color="warning"
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>
    );
  }

  // Show error if site key is not configured in production
  if (!siteKey) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Turnstile site key is not configured. Please check your environment
        variables.
      </Alert>
    );
  }

  // Production mode - render actual Turnstile widget
  return (
    <Box sx={{ my: 2 }}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, fontSize: '0.875rem' }}
      >
        Please verify you&apos;re human to continue:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      >
        <Turnstile
          siteKey={siteKey}
          onSuccess={onSuccess}
          onError={onError}
          onExpire={onExpire}
          options={{
            theme: 'light',
            size: 'normal',
          }}
        />
      </Box>
    </Box>
  );
};

export default TurnstileWidget;
